import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/AuthContext";
import { toast } from "sonner";
import { X, FileText, Eye, DownloadSimple, ArrowSquareOut } from "@phosphor-icons/react";
import { DocumentViewerModal } from "@/components/DocumentViewer";
import { SemaphoreBadge } from "./SemaphoreBadge";

const fmt = (v) => { if (!v) return "—"; const [y, m, d] = String(v).slice(0, 10).split("-"); return d ? `${d}/${m}/${y}` : v; };

async function downloadFile(d) {
  try {
    const r = await api.get(`/documents/file/${d.drive_file_id}`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a"); a.href = url; a.download = d.filename || d.name; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  } catch { toast.error("No se pudo descargar el archivo"); }
}

const DocRow = ({ d, onView }) => (
  <li className="px-5 py-3 flex items-start gap-3 hover:bg-neutral-50" data-testid={`drawer-doc-${d.id}`}>
    <div className="w-9 h-9 bg-blue-50 text-blue-700 rounded-md flex items-center justify-center border border-blue-100 shrink-0"><FileText size={16} weight="duotone"/></div>
    <div className="flex-1 min-w-0">
      <div className="font-semibold text-neutral-900 truncate">{d.name}</div>
      <div className="text-xs text-neutral-500 mt-0.5">{d.period} · {d.authority} · Vence {fmt(d.expiration_date)}</div>
      <div className="mt-1"><SemaphoreBadge status={d.expiration_date ? d.status : "sin_vencimiento"} days={d.days_to_expire} compact/></div>
    </div>
    <div className="flex items-center gap-2 shrink-0 pt-1">
      {d.drive_file_id && <button onClick={() => onView(d)} title="Ver" className="text-indigo-600 hover:text-indigo-800" data-testid={`drawer-view-${d.id}`}><Eye size={16}/></button>}
      {d.drive_file_id && <button onClick={() => downloadFile(d)} title="Descargar" className="text-blue-600 hover:text-blue-800" data-testid={`drawer-download-${d.id}`}><DownloadSimple size={16}/></button>}
      <Link to={`/documentos/${d.id}`} title="Abrir detalle" className="text-neutral-500 hover:text-neutral-900" data-testid={`drawer-open-${d.id}`}><ArrowSquareOut size={16}/></Link>
    </div>
  </li>
);

export default function ObligationDocsDrawer({ obligation, onClose }) {
  const [docs, setDocs] = useState(null);
  const [viewDoc, setViewDoc] = useState(null);

  useEffect(() => {
    api.get(`/obligations/${obligation.id}/detail`)
      .then(r => setDocs([...(r.data.documents || [])].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))))
      .catch(() => setDocs([]));
  }, [obligation.id]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !viewDoc) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, viewDoc]);

  return (
    <div className="fixed inset-0 z-50" data-testid="obligation-docs-drawer">
      <div className="absolute inset-0 bg-neutral-900/40" onClick={onClose}/>
      <aside className="absolute right-0 top-0 h-full w-[480px] max-w-[95vw] bg-white shadow-2xl flex flex-col anim-slide-in-right">
        <div className="px-5 py-4 border-b border-neutral-200 flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="text-[11px] font-bold uppercase tracking-widest text-blue-600">Documentos de la obligación</div>
            <h3 className="font-display text-lg font-bold leading-tight mt-0.5 break-words" data-testid="drawer-obligation-name">{obligation.name}</h3>
            <div className="mt-2"><SemaphoreBadge status={obligation.doc_status} days={obligation.days_to_expire}/></div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full border border-neutral-200 text-neutral-500 hover:text-neutral-900 flex items-center justify-center" data-testid="drawer-close-btn"><X size={16}/></button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {!docs ? <p className="p-5 text-sm text-neutral-400">Cargando…</p>
            : docs.length === 0 ? (
              <div className="p-10 text-center text-neutral-400" data-testid="drawer-empty">
                <FileText size={40} className="mx-auto mb-2 text-neutral-300" weight="duotone"/>
                <p className="text-sm">Esta obligación aún no tiene documentos.</p>
              </div>
            ) : <ul className="divide-y divide-neutral-100">{docs.map(d => <DocRow key={d.id} d={d} onView={setViewDoc}/>)}</ul>}
        </div>
        <div className="px-5 py-3 border-t border-neutral-200 flex justify-between items-center text-xs">
          <span className="text-neutral-500">{docs ? `${docs.length} documento(s)` : ""}</span>
          <Link to={`/obligaciones/${obligation.id}?tab=documents`} className="font-semibold text-blue-600 hover:underline" data-testid="drawer-open-obligation">Abrir obligación completa</Link>
        </div>
      </aside>
      {viewDoc && <DocumentViewerModal docId={viewDoc.id} fileId={viewDoc.drive_file_id} mimeType={viewDoc.mime_type} filename={viewDoc.filename} onClose={() => setViewDoc(null)}/>}
    </div>
  );
}
